/**
 * Delete an output template. The server refuses (409) while a compile is in
 * progress or while workflows are still bound to it; those responses carry the
 * actionable detail (the bound agent names), which renders verbatim rather than
 * as a generic error so the admin knows exactly what to unbind first.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { getApiErrorMessage, type IApiAxiosError } from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

import { useDeleteTemplate } from "./useOutputTemplates";

interface DeleteTemplateDialogProps {
    open: boolean;
    templateId: number;
    templateName?: string;
    onClose: () => void;
    onDeleted?: () => void;
}

export function DeleteTemplateDialog({
    open,
    templateId,
    templateName,
    onClose,
    onDeleted,
}: DeleteTemplateDialogProps) {
    const [error, setError] = useState<string | null>(null);
    const deleteTemplate = useDeleteTemplate();

    useEffect(() => {
        if (open) setError(null);
    }, [open]);

    const handleConfirm = () => {
        setError(null);
        deleteTemplate.mutate(templateId, {
            onSuccess: () => {
                // Close this dialog before onDeleted unmounts the panel subtree
                // (which contains this dialog) to avoid a state update on an
                // unmounting owner.
                onClose();
                onDeleted?.();
            },
            onError: (err) => {
                setError(
                    getApiErrorMessage(err as IApiAxiosError, "Delete failed")
                );
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete template</DialogTitle>
                    <DialogDescription>
                        {templateName
                            ? `“${templateName}” will be removed. Workflows bound to it lose their output.`
                            : "This template will be removed. Workflows bound to it lose their output."}
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <p
                        data-testid="template-delete-error"
                        className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border p-3 text-sm"
                    >
                        {error}
                    </p>
                )}

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={deleteTemplate.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        data-testid="template-delete-confirm"
                        onClick={handleConfirm}
                        disabled={deleteTemplate.isPending}
                    >
                        {deleteTemplate.isPending && (
                            <Loader2
                                className="mr-2 size-4 animate-spin"
                                aria-hidden
                            />
                        )}
                        Delete template
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
